import React, { useState, useEffect } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Loader2, Search, Wand2, AlertTriangle, CheckCircle2, Phone, IdCard,
  Crown, Archive, RefreshCcw, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

export default function DedupPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoMerging, setAutoMerging] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      // Prima normalizza i campi per evitare miss in scan
      await api.post("/dedup/normalize");
      const r = await api.get("/dedup/scan");
      setData(r.data);
      toast.success(`Scan completato: ${r.data.totalGroups} gruppi di duplicati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore scan");
    } finally { setLoading(false); }
  };

  useEffect(() => { scan(); }, []); // eslint-disable-line

  const autoMergeAll = async () => {
    if (!data || data.totalGroups === 0) return;
    if (!window.confirm(`Confermi la fusione automatica di ${data.totalGroups} gruppi (${data.totalDuplicateClients} clienti)?\n\nIl sistema sceglierà automaticamente il cliente più completo come principale e fonderà gli altri.\nI clienti fusi vengono archiviati (non cancellati definitivamente).`)) return;
    setAutoMerging(true);
    try {
      const r = await api.post("/dedup/auto-merge", { dryRun: false });
      toast.success(`✓ Fusi ${r.data.mergedGroups} gruppi (${r.data.mergedClients} clienti archiviati)`);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore auto-merge");
    } finally { setAutoMerging(false); }
  };

  const mergeGroup = async (group, primaryId) => {
    const secondaryIds = group.clients.map((c) => c.id).filter((id) => id !== primaryId);
    if (!window.confirm(`Confermi la fusione di ${secondaryIds.length} clienti nel principale?\nI clienti uniti verranno archiviati.`)) return;
    try {
      const r = await api.post("/dedup/merge", { primary_id: primaryId, secondary_ids: secondaryIds });
      toast.success(`Fusi ${r.data.mergedCount} clienti nel principale`);
      await scan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore merge");
    }
  };

  return (
    <AppLayout
      title="Pulizia Duplicati"
      subtitle="Database hygiene"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={scan} disabled={loading} data-testid="dedup-rescan"
            className="text-sm flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-[3px] hover:bg-slate-50 text-slate-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Riscansiona
          </button>
          {data && data.totalGroups > 0 && (
            <button onClick={autoMergeAll} disabled={autoMerging} data-testid="dedup-auto-merge"
              className="text-sm flex items-center gap-2 px-4 py-2 bg-[#B7955B] hover:bg-[#A18048] text-[#0A192F] font-semibold rounded-[3px] disabled:opacity-60">
              {autoMerging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              Unisci tutti ({data.totalGroups})
            </button>
          )}
        </div>
      }
    >
      {/* Hero summary */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <SumCard icon={Search} tone="info" label="Clienti totali" value={data.totalClients} sub="Nel portafoglio" />
          <SumCard icon={AlertTriangle} tone={data.totalGroups > 0 ? "warn" : "ok"}
                   label="Gruppi duplicati" value={data.totalGroups}
                   sub={data.totalGroups > 0 ? "Da pulire" : "Tutto pulito"} />
          <SumCard icon={Archive} tone="muted" label="Clienti coinvolti" value={data.totalDuplicateClients}
                   sub="Verranno fusi" />
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Scansione in corso...
        </div>
      )}

      {data && data.totalGroups === 0 && (
        <div className="bg-white border border-emerald-300 rounded-[3px] p-10 text-center" data-testid="dedup-clean">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h2 className="font-serif text-2xl text-slate-900">Database pulito</h2>
          <p className="text-slate-600 text-sm mt-2 max-w-xl mx-auto">
            Non ci sono clienti duplicati. Il sistema previene automaticamente i duplicati durante import (Excel/JSON) e segnalazioni webhook.
          </p>
        </div>
      )}

      {data && data.groups && data.groups.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-[3px] p-3 text-sm text-amber-900">
            <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-[#B7955B]" />
            <div>
              <strong>Suggerimento:</strong> usa <em>"Unisci tutti"</em> per fondere automaticamente i gruppi (sceglie il cliente più completo come principale).
              Oppure scegli manualmente il principale per ogni gruppo cliccando sulla corona.
            </div>
          </div>
          {data.groups.map((g, i) => (
            <DupGroup key={i} group={g} onMerge={mergeGroup} />
          ))}
        </div>
      )}
    </AppLayout>
  );
}

function SumCard({ icon: Icon, tone, label, value, sub }) {
  const palette = {
    info:  "border-sky-300 bg-sky-50 text-sky-800",
    warn:  "border-amber-300 bg-amber-50 text-amber-800",
    ok:    "border-emerald-300 bg-emerald-50 text-emerald-800",
    muted: "border-slate-300 bg-white text-slate-700",
  }[tone] || "border-slate-200 bg-white text-slate-700";
  return (
    <div className={`border ${palette} rounded-[3px] p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] opacity-80">{label}</div>
          <div className="font-serif text-3xl tabular-nums mt-1">{value}</div>
          <div className="text-xs mt-0.5 opacity-70">{sub}</div>
        </div>
        <Icon className="w-5 h-5 opacity-70" />
      </div>
    </div>
  );
}

function DupGroup({ group, onMerge }) {
  // Prefer the most complete as default primary
  const sortedByCompleteness = [...group.clients].sort((a, b) => completeness(b) - completeness(a));
  const [primary, setPrimary] = useState(sortedByCompleteness[0]?.id);

  return (
    <div className="bg-white border border-slate-200 rounded-[3px] shadow-[0_1px_3px_rgba(10,25,47,0.05)]" data-testid="dup-group">
      <div className="px-5 py-3 border-b border-[#F0F2F5] flex items-center justify-between bg-slate-50">
        <div className="flex items-center gap-2">
          {group.key === "telefono" ? <Phone className="text-[#B7955B] w-4 h-4" /> : <IdCard className="w-4 h-4" />}
          <h3 className="font-serif text-base text-slate-900">
            {group.key === "telefono" ? "Stesso telefono" : "Stesso CF"}: <span className="text-[#8a6e3d] tabular-nums">{group.value}</span>
          </h3>
          <span className="text-xs text-slate-500">({group.count} duplicati)</span>
        </div>
        <button
          onClick={() => onMerge(group, primary)}
          data-testid="dup-merge-btn"
          className="text-xs flex items-center gap-1.5 px-3 py-1.5 text-white rounded-[3px]" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} 
        >
          <Wand2 className="w-3 h-3" /> Unisci nel principale
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <th className="text-left px-3 py-2 w-16">Princ.</th>
              <th className="text-left px-3 py-2">Cliente</th>
              <th className="text-left px-3 py-2">Telefono</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Stato</th>
              <th className="text-left px-3 py-2">CF</th>
              <th className="text-left px-3 py-2">Citta</th>
              <th className="text-right px-3 py-2">Compl.</th>
              <th className="text-right px-3 py-2">Aggiornato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {group.clients.map((c) => {
              const isPrimary = primary === c.id;
              const score = completeness(c);
              return (
                <tr key={c.id} className={isPrimary ? "bg-[#F5EFE3]/40" : ""} data-testid={`dup-row-${c.id}`}>
                  <td className="px-3 py-2.5">
                    <button
                      onClick={() => setPrimary(c.id)}
                      title="Imposta come principale"
                      className={`p-1.5 rounded-[3px] border transition ${isPrimary
                        ? "border-[#B7955B] bg-[#F5EFE3] text-[#8a6e3d]"
                        : "border-slate-200 text-slate-400 hover:bg-slate-100"}`}
                    >
                      <Crown className={`w-4 h-4 ${isPrimary ? "fill-[#B7955B]" : ""}`} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5">
                    <Link to={`/clienti/${c.id}`} target="_blank" className="text-slate-900 font-medium hover:underline">
                      {c.cognome || ""} {c.nome || ""}
                    </Link>
                    <div className="text-[10px] text-slate-400">id: {c.id.slice(0, 8)}…</div>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-700">{c.telefono || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700 truncate max-w-[180px]">{c.mail || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{c.stato || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700 tabular-nums">{c.cf || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{c.citta || "—"}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      score >= 10 ? "bg-emerald-100 text-emerald-700" : score >= 5 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                    }`}>{score}</span>
                  </td>
                  <td className="px-3 py-2.5 text-right text-[10px] text-slate-500 tabular-nums">{(c.updated_at || "").slice(0, 10)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function completeness(c) {
  const keys = ["nome", "cognome", "telefono", "telefono2", "mail", "nascita", "cf",
    "citta", "indirizzo", "atc", "stato", "data", "dpr", "fonte", "note"];
  return keys.filter((k) => (c?.[k] || "").trim?.()).length
    + Math.min(3, (c?.updates || []).length);
}
